/**
 * PhoneApp: composes the session, relay, SIP, media, keepalive and lifecycle pieces and
 * exposes signals for the UI. It owns the sequencing rules of the design:
 *
 * - node_state restarting|down holds SIP reconnects; `up` releases them with jitter
 * - session.revoked superseded -> ua.stop() (no un-REGISTER), relay stopped, no reconnect loop
 * - JWT refresh at 50 %; a rotated SIP credential is applied only with no line up
 * - every relay command shows a spinner (pending kinds) and toasts on nack / timeout
 */
import { computed, signal } from '@preact/signals';
import { Inviter, RegistererState, UserAgent, type Invitation, type Notification, type Registerer } from 'sip.js';
import { LIMITS, TIMINGS, type CallSummary, type NodeState, type RelayEvent } from '@ccphone/contracts';
import type { PhoneEnv } from '../env.js';
import { APP_VERSION } from '../env.js';
import type { Logger } from '../log.js';
import { ApiClient } from './api.js';
import { ServerClock } from './clock.js';
import { applyRuntimeCsp } from '../csp.js';
import { Lifecycle, guardClose } from './lifecycle.js';
import { SessionRefresher, type RefreshResult } from './session-refresh.js';
import type { SessionStore, StoredSession } from './session-store.js';
import { toStoredSession } from './launch.js';
import { z } from 'zod';
import { RelayClient, RelayCommandError, type AckEvent, type CommandInput, type RelayStatus } from '../relay/client.js';
import { createPhoneStore, type PhoneStore } from '../relay/state.js';
import { callsAwaitingLeg } from '../relay/reducer.js';
import { createUserAgent, DTMF_OPTIONS } from '../sip/user-agent.js';
import { SipReconnector, type SipStateName } from '../sip/reconnect.js';
import { LineManager, OWN_DIAL_WINDOW_MS, type InvitationLike, type InviterLike, type LineView } from '../sip/lines.js';
import { Ringback } from '../sip/ringback.js';
import { LoopbackKeepalive } from '../keepalive/loopback.js';
import { holdWebLock, type HeldLock } from '../keepalive/web-lock.js';
import { realTimers, type TimerHost } from '../keepalive/timers.js';
import { AudioEngine } from '../media/audio.js';
import { listDevices, loadDevicePrefs, micConstraints, resolveDevice, saveDevicePrefs, watchDeviceChange, type DeviceLists, type DevicePrefs } from '../media/devices.js';
import { HidHeadset, listHidDevices, requestHeadset } from '../media/hid.js';
import { InstallPrompt } from './install.js';
import { registerServiceWorker } from './sw-register.js';
import { subscribePush } from './push.js';
import { toDialTarget } from '../ui/format.js';
import { Router } from './router.js';
import { probeTurn, type TurnProbeResult } from '../media/turn-probe.js';

export interface Toast {
  id: number;
  kind: 'info' | 'error';
  text: string;
}

export type Phase = 'booting' | 'ready' | 'superseded' | 'relaunch';

export interface PhoneAppDeps {
  env: PhoneEnv;
  log: Logger;
  store: SessionStore;
  session: StoredSession;
  timers?: TimerHost;
  fetchFn?: typeof fetch;
  vapidPublicKey?: string | null;
}

export class PhoneApp {
  readonly phase = signal<Phase>('booting');
  readonly relaunchReason = signal<string | null>(null);
  readonly supersededAt = signal<string | null>(null);
  readonly sipState = signal<SipStateName>('connecting');
  readonly relayStatus = signal<RelayStatus>('idle');
  readonly lines = signal<LineView[]>([]);
  readonly toasts = signal<Toast[]>([]);
  readonly pendingKinds = signal<ReadonlySet<string>>(new Set());
  readonly devices = signal<DeviceLists>({ mics: [], outputs: [] });
  readonly devicePrefs = signal<DevicePrefs>(loadDevicePrefs());
  readonly micDenied = signal(false);
  readonly hidDevices = signal<HIDDevice[]>([]);
  readonly keepaliveHealthy = signal(false);
  readonly turnStatus = signal<TurnProbeResult | 'unknown'>('unknown');
  readonly soundNeedsClick = computed(() => this.audio.state.value === 'suspended');
  readonly agent: StoredSession['launch']['agent'];
  readonly store: PhoneStore = createPhoneStore();
  readonly audio: AudioEngine;
  readonly clock: ServerClock;
  readonly router: Router;
  readonly install = new InstallPrompt();
  readonly lifecycle: Lifecycle;
  readonly linesUp = computed(() => this.lines.value.filter((l) => l.state === 'up' || l.state === 'early').length);
  readonly clickToDialDisabled = computed(() => {
    const m = this.store.maintenance.value;
    if (!m.active) return false;
    if (!m.restart_at) return true;
    return this.clock.until(m.restart_at) <= 2 * 60_000;
  });

  private session: StoredSession;
  private readonly api: ApiClient;
  private relay!: RelayClient;
  private ua: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private reconnector!: SipReconnector;
  private readonly lineManager: LineManager;
  private readonly refresher: SessionRefresher;
  private readonly keepalive: LoopbackKeepalive;
  private lock: HeldLock | null = null;
  private ringback!: Ringback;
  private ownDialAt = 0;
  private pendingSip: RefreshResult['sip'] | undefined;
  private pendingTurn: RefreshResult['turn'] | undefined;
  private toastSeq = 0;
  private readonly timers: TimerHost;
  private unregisterOnStop = false;
  private hid: HidHeadset | null = null;
  private unguard: (() => void) | null = null;
  private unwatchDevices: (() => void) | null = null;
  private swReg: ServiceWorkerRegistration | null = null;
  private stopped = false;

  constructor(private readonly d: PhoneAppDeps) {
    this.timers = d.timers ?? realTimers;
    this.session = d.session;
    this.agent = d.session.launch.agent;
    this.clock = new ServerClock();
    this.clock.sync(d.session.launch.server_time, Date.parse(d.session.stored_at) || Date.now());
    this.api = new ApiClient({ baseUrl: d.session.launch.api_base || d.env.apiBase, fetchFn: d.fetchFn, getJwt: () => this.session.launch.jwt });
    this.audio = new AudioEngine();
    this.router = new Router();
    this.lifecycle = new Lifecycle({
      timers: this.timers,
      onResume: (gap) => {
        this.d.log.info({ gap_ms: gap }, 'resume detected');
        this.kick();
      },
      onOnline: () => this.kick(),
      onVisibility: () => this.relay.heartbeatNow(),
    });
    this.ringback = new Ringback({ setTone: (on) => this.audio.setRingbackTone(on) });
    this.lineManager = new LineManager({
      maxLines: d.session.launch.policy.maxLines || LIMITS.maxLines,
      clock: this.clock,
      timers: this.timers,
      log: d.log,
      relay: { send: (cmd) => this.command(cmd) },
      audio: {
        ringing: (on) => this.audio.ringing(on),
        callWaitingBeep: () => this.audio.callWaitingBeep(),
        ringback: this.ringback,
        attachRemote: (id, s) => this.audio.attachRemote(id, s),
        detachRemote: (id) => this.audio.detachRemote(id),
        setRemoteMuted: (id, m) => this.audio.setRemoteMuted(id, m),
      },
      mediaConstraints: () => micConstraints(this.devicePrefs.value),
      matchCall: (header, attached) => this.matchCall(header, attached),
      onChange: (lines) => this.onLinesChanged(lines),
      ownDialPending: () => this.clock.now() - this.ownDialAt < OWN_DIAL_WINDOW_MS,
      inviterFactory: (target, constraints) => this.makeInviter(target, constraints),
    });
    this.refresher = new SessionRefresher({
      api: this.api,
      clock: this.clock,
      timers: this.timers,
      log: d.log,
      getRefreshToken: () => this.session.launch.refresh_token,
      getExpiresAt: () => this.session.launch.expires_at,
      getIssuedAt: () => this.session.launch.server_time,
      linesUp: () => this.lineManager.linesUp(),
      onRefreshed: (r) => this.onRefreshed(r),
      onExpired: (reason) => this.toRelaunch(reason),
    });
    this.keepalive = new LoopbackKeepalive({ timers: this.timers, log: d.log });
    this.buildRelay();
    this.buildReconnector();
  }

  // ---- composition --------------------------------------------------------------

  private buildRelay(): void {
    this.relay = new RelayClient({
      url: this.session.launch.relay.url,
      role: 'phone',
      getJwt: () => this.session.launch.jwt,
      heartbeat: () => ({
        sip: this.sipState.value,
        audio_ok: this.audio.unlocked && !this.micDenied.value,
        visibility: this.lifecycle.visibility.value,
        locked: this.lifecycle.locked.value,
        idle: this.lifecycle.idle.value,
        lines_up: Math.min(2, this.lineManager.linesUp()),
      }),
      onEvent: (ev) => this.onRelayEvent(ev),
      onStatus: (s) => {
        this.relayStatus.value = s;
        if (s === 'open') this.lineManager.rematch();
      },
      log: this.d.log.child({ component: 'relay' }),
      timers: this.timers,
    });
  }

  private buildReconnector(): void {
    this.reconnector = new SipReconnector({
      reconnect: async () => {
        if (!this.ua) throw new Error('no user agent');
        await this.ua.reconnect();
      },
      register: () => this.registerWithDelegate(),
      relayHealth: () => ({ healthy: this.relay.healthy, control: this.store.control.value }),
      onState: (s) => {
        this.sipState.value = s;
      },
      onRevoked: (reason) => this.toRelaunch(reason),
      log: this.d.log.child({ component: 'sip' }),
      timers: this.timers,
    });
  }

  private buildUserAgent(): void {
    const b = this.session.launch;
    const { ua, registerer } = createUserAgent(
      { sip: b.sip, turn: b.turn, policy: b.policy },
      {
        version: APP_VERSION,
        delegate: {
          onInvite: (inv: Invitation) => {
            this.lineManager.onInvite(inv as unknown as InvitationLike);
          },
          onConnect: () => this.d.log.info('SIP transport connected'),
          onDisconnect: (err) => {
            this.d.log.warn({ err: err?.message }, 'SIP transport disconnected');
            if (err) this.reconnector.onDisconnected();
          },
          onNotify: (n: Notification) => this.onNotify(n),
        },
        logConnector: (level, category, _label, content) => {
          const fn = level === 'error' ? this.d.log.error : level === 'warn' ? this.d.log.warn : this.d.log.debug;
          fn.call(this.d.log, { category }, content);
        },
      },
    );
    registerer.stateChange.addListener((s) => {
      if (s === RegistererState.Registered) this.reconnector.onRegistered();
      else if (s === RegistererState.Unregistered && !this.stopped && !this.unregisterOnStop) {
        // the automatic refresh does not report the status code: re-REGISTER once with a delegate to learn it
        this.sipState.value = 'unregistered';
        this.registerWithDelegate().catch(() => undefined);
      }
    });
    this.ua = ua;
    this.registerer = registerer;
  }

  private async registerWithDelegate(): Promise<void> {
    const r = this.registerer;
    if (!r) throw new Error('no registerer');
    await r.register({
      requestDelegate: {
        onReject: (resp) => {
          this.reconnector.onRegisterRejected(resp.message.statusCode ?? 0);
        },
      },
    });
  }

  private makeInviter(target: string, constraints: MediaStreamConstraints): InviterLike {
    if (!this.ua) throw new Error('no user agent');
    const uri = UserAgent.makeURI(`sip:${target}@${this.session.launch.sip.domain}`);
    if (!uri) throw new Error('invalid target');
    const inviter = new Inviter(this.ua, uri, { earlyMedia: true, sessionDescriptionHandlerOptions: { constraints } });
    return inviter as unknown as InviterLike;
  }

  // ---- lifecycle ------------------------------------------------------------------

  async start(): Promise<void> {
    const b = this.session.launch;
    applyRuntimeCsp(b);
    this.router.start((lt) => this.d.log.info({ lt_len: lt.length }, 'launch token received while a session exists: ignored'));
    this.lifecycle.start();
    this.install.start(window, safeStorage());
    this.lock = holdWebLock();
    this.relay.start();
    this.buildUserAgent();
    void this.startSip();
    this.refresher.start();
    void this.keepalive.start().then(() => {
      this.keepaliveHealthy.value = this.keepalive.healthy;
    });
    this.timers.setInterval(() => {
      this.keepaliveHealthy.value = this.keepalive.healthy;
    }, 60_000);
    void this.refreshDevices();
    this.unwatchDevices = watchDeviceChange(navigator.mediaDevices, () => void this.onDeviceChange());
    this.unguard = guardClose(window, () => this.lineManager.linesUp() > 0, () => this.teardownOnClose());
    window.addEventListener('message', (ev) => this.onWindowMessage(ev));
    void registerServiceWorker(this.api.baseUrl, this.d.log).then(async (reg) => {
      this.swReg = reg;
      if (reg) await subscribePush(reg, this.api, this.session.launch.push?.vapid_public_key ?? this.d.vapidPublicKey ?? null, this.d.log);
      reg?.active?.postMessage({ t: 'ccphone:clear-badge' });
    });
    void this.turnProbeLoop();
    void listHidDevices().then((ds) => {
      this.hidDevices.value = ds;
      const first = ds[0];
      if (first) void this.useHid(first);
    });
    this.phase.value = 'ready';
  }

  /** relay-only probe at login, retried every 60 s while it fails */
  private async turnProbeLoop(): Promise<void> {
    if (this.stopped) return;
    const r = await probeTurn(this.session.launch.turn).catch((): TurnProbeResult => 'unsupported');
    this.turnStatus.value = r;
    if (r === 'unreachable') this.timers.setTimeout(() => void this.turnProbeLoop(), 60_000);
  }

  private async startSip(): Promise<void> {
    if (!this.ua) return;
    this.sipState.value = 'connecting';
    try {
      await this.ua.start();
      await this.registerWithDelegate();
    } catch (e) {
      this.d.log.warn({ err: (e as Error).message }, 'initial SIP start failed');
      this.reconnector.onDisconnected();
    }
  }

  /** stop everything; `unregister` only on a normal logout */
  async stop(unregister = false): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    this.unregisterOnStop = unregister;
    this.reconnector.stop();
    this.refresher.stop();
    this.relay.stop();
    this.lineManager.stop();
    this.keepalive.stop();
    this.lifecycle.stop();
    this.unguard?.();
    this.unwatchDevices?.();
    this.lock?.release();
    if (unregister && this.registerer) await this.registerer.unregister().catch(() => undefined);
    await this.ua?.stop().catch(() => undefined);
    this.ua = null;
    this.registerer = null;
    this.audio.ringing(false);
  }

  private teardownOnClose(): void {
    for (const l of this.lines.value) if (l.state !== 'ended') void this.lineManager.hangup(l.id);
  }

  /** sleep/resume, IP change, Retry now */
  kick(): void {
    this.relay.kick();
    this.reconnector.kick();
  }

  retryNow(): void {
    this.kick();
  }

  // ---- relay events ---------------------------------------------------------------

  private onRelayEvent(ev: RelayEvent): void {
    const before = this.store.state.value;
    this.store.applyEvent(ev);
    switch (ev.t) {
      case 'state':
      case 'call.ringing':
      case 'call.dialing':
        this.lineManager.rematch();
        if (ev.t === 'state') this.reconnector.setNodeState(ev.node_state);
        break;
      case 'node_state':
        this.onNodeState(ev.state);
        break;
      case 'call.ended':
        this.lineManager.endByCallId(ev.call_id, ev.outcome);
        this.swReg?.active?.postMessage({ t: 'ccphone:call-ended', call_id: ev.call_id });
        break;
      case 'call.missed':
        this.lineManager.endByCallId(ev.call_id, 'missed');
        this.swReg?.active?.postMessage({ t: 'ccphone:call-ended', call_id: ev.call_id });
        break;
      case 'call.held':
        this.lineManager.setRemoteHeld(ev.call_id, true);
        break;
      case 'call.resumed':
        this.lineManager.setRemoteHeld(ev.call_id, false);
        break;
      case 'session.revoked':
        this.onRevoked(ev.reason, ev.at);
        break;
      case 'maintenance':
        if (ev.active && !before.maintenance.active) this.toast('info', ev.message ?? 'Maintenance scheduled');
        break;
      default:
        break;
    }
  }

  private onNodeState(s: NodeState): void {
    this.reconnector.setNodeState(s);
    if (s === 'restarting' || s === 'down') {
      // T-0: the node drains; any call still up is over. No Rejoin after a node restart.
      this.lineManager.endAll('server_restart');
    }
  }

  private onRevoked(reason: 'logout' | 'admin' | 'zombie' | 'expired' | 'superseded', at: string): void {
    this.d.log.warn({ reason }, 'session revoked');
    if (reason === 'superseded') {
      this.supersededAt.value = at;
      this.phase.value = 'superseded';
      void this.stop(false); // ua.stop() without un-REGISTER, shield released, no reconnect loop
      return;
    }
    this.toRelaunch(reason);
  }

  private toRelaunch(reason: string): void {
    if (this.phase.value === 'relaunch') return;
    this.relaunchReason.value = reason;
    this.phase.value = 'relaunch';
    void this.stop(false);
    void this.d.store.clear().catch(() => undefined);
  }

  /** "Use this device": try the refresh path; a revoked refresh token lands on the re-launch card */
  async useThisDevice(): Promise<void> {
    this.toast('info', 'Re-opening the phone on this device...');
    const ok = await this.refresher.refreshNow().catch(() => false);
    if (!ok) this.toRelaunch('superseded');
    else window.location.reload();
  }

  // ---- MWI over SIP NOTIFY ---------------------------------------------------------

  private onNotify(n: Notification): void {
    n.accept().catch(() => undefined);
    const req = n.request;
    if (req.getHeader('Event')?.toLowerCase() !== 'message-summary') return;
    const body = req.body ?? '';
    const waiting = /messages-waiting:\s*yes/i.test(body);
    const m = /voice-message:\s*(\d+)\/(\d+)/i.exec(body);
    this.store.applyEvent({ t: 'mwi', at: new Date(this.clock.now()).toISOString(), waiting, new: m ? Number(m[1]) : 0, old: m ? Number(m[2]) : 0 });
  }

  // ---- session refresh ------------------------------------------------------------------

  private onRefreshed(r: RefreshResult): void {
    this.clock.sync(r.server_time);
    const launch = { ...this.session.launch, jwt: r.jwt, refresh_token: r.refresh_token, expires_at: r.expires_at, server_time: r.server_time };
    if (r.turn) launch.turn = r.turn;
    this.session = toStoredSession(launch, this.session.browser_id, this.clock.offset);
    void this.d.store.put(this.session).catch((e: Error) => this.d.log.warn({ err: e.message }, 'session store write failed'));
    if (r.sip) {
      this.pendingSip = r.sip;
      this.pendingTurn = r.turn;
      this.applyPendingCredentials();
    }
  }

  /** SIP credential swap only with no line up */
  private applyPendingCredentials(): void {
    if (!this.pendingSip || this.lineManager.linesUp() > 0 || this.lineManager.activeCount() > 0) return;
    const sip = this.pendingSip;
    this.pendingSip = undefined;
    const launch = { ...this.session.launch, sip, ...(this.pendingTurn ? { turn: this.pendingTurn } : {}) };
    this.pendingTurn = undefined;
    this.session = toStoredSession(launch, this.session.browser_id, this.clock.offset);
    void this.d.store.put(this.session).catch(() => undefined);
    this.d.log.info('SIP credential swap');
    const old = this.ua;
    this.reconnector.stop();
    this.buildReconnector();
    void old?.stop().catch(() => undefined);
    this.buildUserAgent();
    void this.startSip();
  }

  // ---- lines ------------------------------------------------------------------------------

  private onLinesChanged(lines: LineView[]): void {
    this.lines.value = lines;
    if (lines.every((l) => l.state === 'ended')) this.applyPendingCredentials();
    this.relay.heartbeatNow();
    const up = lines.some((l) => l.state === 'up');
    const muted = lines.some((l) => l.state === 'up' && l.muted);
    const ringing = lines.some((l) => l.state === 'ringing');
    void this.hid?.setLeds({ offHook: up, mute: muted, ring: ringing });
  }

  private matchCall(header: string | null, attached: ReadonlySet<string>): CallSummary | undefined {
    const s = this.store.state.value;
    if (header && s.calls[header]) return s.calls[header];
    const waiting = callsAwaitingLeg(s, attached);
    return waiting[0];
  }

  // ---- commands ---------------------------------------------------------------------------

  /** relay command with spinner + toast; rejects on nack / timeout */
  async command(cmd: CommandInput): Promise<AckEvent> {
    const kind = cmd.t;
    this.setPending(kind, true);
    try {
      return await this.relay.send(cmd);
    } catch (e) {
      if (e instanceof RelayCommandError) {
        if (kind !== 'stats' && kind !== 'answer') this.toast('error', e.kind === 'timeout' ? 'No response from the phone server' : `Could not ${kind.replace(/[._]/g, ' ')}: ${e.code ?? 'rejected'}`);
      }
      throw e;
    } finally {
      this.setPending(kind, false);
    }
  }

  private setPending(kind: string, on: boolean): void {
    const next = new Set(this.pendingKinds.value);
    if (on) next.add(kind);
    else next.delete(kind);
    this.pendingKinds.value = next;
  }

  toast(kind: Toast['kind'], text: string): void {
    const id = ++this.toastSeq;
    this.toasts.value = [...this.toasts.value, { id, kind, text }];
    this.timers.setTimeout(() => {
      this.toasts.value = this.toasts.value.filter((t) => t.id !== id);
    }, 5000);
  }

  async dial(raw: string, crmRef?: string): Promise<void> {
    const target = toDialTarget(raw);
    if (!target) {
      this.toast('error', 'Enter a number to dial');
      return;
    }
    if (this.clickToDialDisabled.value) {
      this.toast('error', 'Dialling is paused for the server restart');
      return;
    }
    if (this.lineManager.activeCount() >= (this.session.launch.policy.maxLines || LIMITS.maxLines)) {
      this.toast('error', 'Both lines are in use');
      return;
    }
    await this.audio.unlock();
    if (this.d.env.directDial) {
      this.lineManager.invite(target);
      return;
    }
    this.ownDialAt = this.clock.now();
    try {
      await this.command({ t: 'dial', number: target, ...(crmRef ? { crm_ref: crmRef } : {}) });
    } catch {
      this.ownDialAt = 0;
    }
  }

  answer(lineId: string): Promise<void> {
    void this.audio.unlock();
    return this.lineManager.answer(lineId);
  }
  decline(lineId: string, toVoicemail = false): Promise<void> {
    return this.lineManager.decline(lineId, toVoicemail);
  }
  hangup(lineId: string): Promise<void> {
    return this.lineManager.hangup(lineId);
  }
  hold(lineId: string, on: boolean): Promise<void> {
    return this.lineManager.hold(lineId, on).catch(() => undefined);
  }
  mute(lineId: string, on: boolean): Promise<void> {
    return this.lineManager.mute(lineId, on);
  }
  dtmf(lineId: string, tones: string): boolean {
    return this.lineManager.sendDtmf(lineId, tones);
  }
  get dtmfOptions(): typeof DTMF_OPTIONS {
    return DTMF_OPTIONS;
  }

  async transferBlind(callId: string, target: string): Promise<void> {
    await this.command({ t: 'transfer.blind', call_id: callId, target }).catch(() => undefined);
  }
  async consultStart(callId: string, target: string): Promise<void> {
    await this.command({ t: 'transfer.attended.start', call_id: callId, target }).catch(() => undefined);
  }
  async consultComplete(callId: string): Promise<void> {
    await this.command({ t: 'transfer.attended.complete', call_id: callId }).catch(() => undefined);
  }
  async swap(callId: string): Promise<void> {
    await this.command({ t: 'swap', call_id: callId }).catch(() => undefined);
  }
  async merge(callId: string): Promise<void> {
    await this.command({ t: 'conf.merge', call_id: callId }).catch(() => undefined);
  }
  async addParty(callId: string, target: string): Promise<void> {
    await this.command({ t: 'conf.add', call_id: callId, target }).catch(() => undefined);
  }
  async partyMute(callId: string, channelId: string, muted: boolean): Promise<void> {
    await this.command({ t: 'conf.mute', call_id: callId, channel_id: channelId, muted }).catch(() => undefined);
  }
  async partyDrop(callId: string, channelId: string): Promise<void> {
    await this.command({ t: 'conf.drop', call_id: callId, channel_id: channelId }).catch(() => undefined);
  }
  async rejoin(callId: string, bridgeId: string): Promise<void> {
    await this.audio.unlock();
    this.ownDialAt = this.clock.now();
    await this.command({ t: 'rejoin', call_id: callId, bridge_id: bridgeId }).catch(() => undefined);
  }
  async setPresence(status: 'available' | 'away' | 'business_trip' | 'do_not_disturb' | 'lunch_break' | 'off_work'): Promise<void> {
    await this.command({ t: 'presence.set', status }).catch(() => undefined);
  }
  async pauseQueues(paused: boolean, reason?: string): Promise<void> {
    await this.command({ t: 'queue.pause', paused, ...(reason ? { reason } : {}) }).catch(() => undefined);
  }
  async resumeSession(): Promise<void> {
    await this.command({ t: 'resume' }).catch(() => undefined);
  }

  async logout(): Promise<void> {
    try {
      await this.api.request('POST', '/v1/session/logout', zAny, undefined);
    } catch {
      /* best effort */
    }
    await this.stop(true);
    await this.d.store.clear().catch(() => undefined);
    this.toRelaunch('logout');
  }

  // ---- devices and sound ---------------------------------------------------------------------

  /** the login click: resumes the AudioContext and asks for the mic once */
  async enableSound(): Promise<void> {
    await this.audio.unlock();
    await this.audio.setSinks(this.devicePrefs.value);
    await this.probeMic();
  }

  async probeMic(): Promise<boolean> {
    try {
      const s = await navigator.mediaDevices.getUserMedia(micConstraints(this.devicePrefs.value));
      for (const t of s.getTracks()) t.stop();
      if (this.micDenied.value) {
        this.micDenied.value = false;
        await this.command({ t: 'set_state', ready: true }).catch(() => undefined);
      }
      await this.refreshDevices();
      return true;
    } catch (e) {
      this.d.log.warn({ err: (e as Error).message }, 'microphone unavailable');
      if (!this.micDenied.value) {
        this.micDenied.value = true;
        await this.command({ t: 'set_state', ready: false, reason: 'mic_denied' }).catch(() => undefined);
      }
      return false;
    }
  }

  async refreshDevices(): Promise<void> {
    try {
      this.devices.value = await listDevices();
    } catch {
      /* enumerate unsupported */
    }
  }

  async setDevicePrefs(p: DevicePrefs): Promise<void> {
    this.devicePrefs.value = p;
    saveDevicePrefs(p);
    await this.audio.setSinks(p);
    await this.lineManager.replaceMicTrack(() => this.newMicTrack());
  }

  private async newMicTrack(): Promise<MediaStreamTrack> {
    const s = await navigator.mediaDevices.getUserMedia(micConstraints(this.devicePrefs.value));
    const t = s.getAudioTracks()[0];
    if (!t) throw new Error('no audio track');
    return t;
  }

  private async onDeviceChange(): Promise<void> {
    await this.refreshDevices();
    const p = this.devicePrefs.value;
    const mic = resolveDevice(p.mic, this.devices.value.mics);
    if (mic !== p.mic) {
      // the chosen mic went away: fall back to the default and replace the track on live lines
      this.devicePrefs.value = { ...p, mic };
      await this.lineManager.replaceMicTrack(() => this.newMicTrack());
      this.toast('info', 'Microphone changed');
    }
    await this.audio.setSinks(this.devicePrefs.value);
  }

  async pairHeadset(): Promise<void> {
    const dev = await requestHeadset();
    if (dev) await this.useHid(dev);
    this.hidDevices.value = await listHidDevices();
  }

  private async useHid(dev: HIDDevice): Promise<void> {
    try {
      const h = new HidHeadset(dev, {
        onHook: (offHook) => {
          const ringing = this.lines.value.find((l) => l.state === 'ringing');
          const up = this.lines.value.find((l) => l.state === 'up');
          if (offHook && ringing) void this.answer(ringing.id);
          else if (!offHook && up) void this.hangup(up.id);
        },
        onMute: (m) => {
          const up = this.lines.value.find((l) => l.state === 'up');
          if (up) void this.mute(up.id, m);
        },
      });
      await h.open();
      this.hid = h;
    } catch (e) {
      this.d.log.warn({ err: (e as Error).message }, 'HID open failed');
    }
  }

  // ---- dock fast path ------------------------------------------------------------------------

  private onWindowMessage(ev: MessageEvent): void {
    if (ev.origin !== window.location.origin) return;
    const d = ev.data as { t?: string } | null;
    if (!d || typeof d !== 'object') return;
    if (d.t === 'ccphone:ping' && ev.source && 'postMessage' in ev.source) {
      (ev.source as Window).postMessage({ t: 'ccphone:pong' }, ev.origin);
    }
  }

  get sessionInfo(): { sid: string | null; expires_at: string; hard_cap_at: string } {
    return { sid: null, expires_at: this.session.launch.expires_at, hard_cap_at: this.session.launch.hard_cap_at };
  }

  get launch(): StoredSession['launch'] {
    return this.session.launch;
  }

  /** heartbeat interval (for the UI's "last heartbeat" indicator) */
  readonly heartbeatMs = TIMINGS.heartbeatIntervalMs;
}

const zAny = z.unknown();

function safeStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}
